<template>
  <div class="schedule-viewing-appointments-form mb-3">
    <div
      v-for="(row, index) in rows"
      :key="row.locked ? ('locked-' + row.appointmentId) : ('new-' + index)"
      class="row align-items-end mb-2"
      :class="{ 'schedule-row-locked': row.locked }">
      <div class="col-12 col-md-2 mb-2">
        <coozzy-form-input
          v-model="row.date"
          type="date"
          :name="$tc('message.marketingObjects.viewingAppointments.date')"
          :is-read-only="row.locked"
          :state="rowErrors[index].date === false ? false : null"
          @input="onRowChanged(index)" />
      </div>
      <div class="col-6 col-md-1 mb-2">
        <label>{{ $t('message.marketingObjects.viewingAppointments.from') }}*</label>
        <coozzy-form-select
          v-model="row.from"
          :disabled="row.locked"
          :state="rowErrors[index].from === false ? false : null"
          @input="onRowChanged(index)">
          <option value="">
            -
          </option>
          <option
            v-for="t in timeList"
            :key="'from-' + index + '-' + t"
            :value="t">
            {{ t }}
          </option>
        </coozzy-form-select>
      </div>
      <div class="col-6 col-md-1 mb-2">
        <label>{{ $t('message.marketingObjects.viewingAppointments.to') }}*
          <coozzy-info-circle-icon
            v-b-tooltip.hover="$t('message.marketingObjects.viewingAppointments.endTimeTooltip')" /></label>
        <coozzy-form-select
          v-model="row.to"
          :disabled="row.locked"
          :state="rowErrors[index].to === false ? false : null"
          @input="onRowChanged(index)">
          <option value="">
            -
          </option>
          <option
            v-for="t in timeList"
            :key="'to-' + index + '-' + t"
            :value="t">
            {{ t }}
          </option>
        </coozzy-form-select>
      </div>
      <div class="col-12 col-md-2 mb-2">
        <coozzy-form-input
          v-model="row.maxParticipants"
          type="number"
          :min="1"
          :name="$tc('message.marketingObjects.viewingAppointments.maxParticipants')"
          :is-read-only="row.locked"
          :state="rowErrors[index].maxParticipants === false ? false : null"
          @input="onRowChanged(index)" />
      </div>
      <div class="col-10 col-md-5 mb-2">
        <label>{{ $t('message.marketingObjects.viewingAppointments.whoIsLeading') }}*</label>
        <coozzy-form-select
          v-model="row.leaderId"
          :disabled="row.locked"
          :state="rowErrors[index].leaderId === false ? false : null"
          @input="onRowChanged(index)">
          <option value="">
            -
          </option>
          <option
            v-if="hasViewingContact"
            :value="CONTACT_PERSON_LEADER">
            {{ viewingContactOptionLabel }}
          </option>
          <option
            v-for="(user, userIndex) in activeUsers"
            :key="'leader-' + index + '-' + userIndex"
            :value="user.id">
            {{ user.firstName }} {{ user.lastName }}
          </option>
        </coozzy-form-select>
      </div>
      <div class="col-2 col-md-1 mb-2 d-flex align-items-center justify-content-end pt-md-4">
        <coozzy-button
          v-if="!row.locked && editableRows.length > 1"
          design="transparent"
          class="p-0"
          @click="removeRow(index)">
          <coozzy-delete-icon />
        </coozzy-button>
      </div>
    </div>
    <div class="row mb-3">
      <div class="col-12 col-md-auto">
        <coozzy-button
          design="default"
          @click="addRow">
          + {{ $t('message.marketingObjects.viewingAppointments.addAppointment') }}
        </coozzy-button>
      </div>
    </div>
    <div class="row">
      <div class="col-10">
        <coozzy-button
          design="default"
          :disabled="saving"
          @click="$emit('cancel')">
          {{ $t('message.generic.cancel') }}
        </coozzy-button>
      </div>
      <div class="col-2">
        <coozzy-button
          design="primary"
          :disabled="saving"
          @click="save">
          {{ $t('message.generic.form.save') }}
        </coozzy-button>
      </div>
    </div>
    <b-modal
      id="informViewingContactModal"
      ref="informViewingContactModal"
      no-close-on-backdrop
      hide-footer
      :title="$t('message.marketingObjects.viewingAppointments.informContactModal.title')">
      <p>{{ $t('message.marketingObjects.viewingAppointments.informContactModal.description') }}</p>
      <div class="col pl-0">
        <coozzy-button
          size="small"
          design="default"
          :disabled="saving"
          @click="onInformContactCancel">
          {{ $t('message.marketingObjects.viewingAppointments.informContactModal.cancel') }}
        </coozzy-button>
        <coozzy-button
          size="small"
          class="float-right mb-0"
          design="prop-green"
          :disabled="saving"
          @click="onInformContactConfirm(true)">
          {{ $t('message.marketingObjects.viewingAppointments.informContactModal.inform') }}
        </coozzy-button>
        <coozzy-button
          size="small"
          class="float-right mb-0 mr-3"
          design="default"
          :disabled="saving"
          @click="onInformContactConfirm(false)">
          {{ $t('message.marketingObjects.viewingAppointments.informContactModal.doNotInform') }}
        </coozzy-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Vue from 'vue'
import CoozzyButton from '@/framework/components/button/CoozzyButton'
import CoozzyFormInput from '@/framework/components/form/input/CoozzyFormInput'
import CoozzyFormSelect from '@/framework/components/form/select/CoozzyFormSelect'
import CoozzyDeleteIcon from '@/framework/components/icons/CoozzyDeleteIcon'
import CalendarSchedulerApi from '@/misc/apis/CalendarSchedulerApi'
import { dateUtils } from '@/mixins/dateUtils'
import {
  buildQuarterHourTimeList,
  isRowStartInPast,
  padTimePart,
  parseTime,
  rowsOverlap,
  timeToMinutes
} from '@/misc/utils/viewingAppointmentSlotUtils'
import { SchedulerAppointmentStatus } from '@/types/api'

export const CONTACT_PERSON_LEADER = '__contact_person__'

function createEmptyRow(defaultLeaderId = '') {
  return {
    date: '',
    from: '',
    to: '',
    maxParticipants: '',
    leaderId: defaultLeaderId,
    locked: false,
    appointmentId: ''
  }
}

export default {
  name: 'ScheduleViewingAppointmentsForm',
  components: {
    CoozzyButton,
    CoozzyFormInput,
    CoozzyFormSelect,
    CoozzyDeleteIcon
  },
  mixins: [dateUtils],
  props: {
    objectId: {
      type: String,
      required: true
    },
    activeUsers: {
      type: Array,
      default: () => []
    },
    viewingContact: {
      type: Object,
      default: null
    },
    currentUserId: {
      type: String,
      default: ''
    },
    addMore: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      CONTACT_PERSON_LEADER,
      rows: [createEmptyRow()],
      rowErrors: [{ date: null, from: null, to: null, leaderId: null, maxParticipants: null }],
      timeList: [],
      saving: false,
      loadingExisting: false,
      existingAppointments: []
    }
  },
  computed: {
    editableRows() {
      return this.rows.filter(row => !row.locked)
    },
    hasViewingContact() {
      if (!this.viewingContact) {
        return false
      }
      return !!(this.viewingContact.id || (this.viewingContact.displayName || '').trim())
    },
    viewingContactOptionLabel() {
      if (!this.viewingContact) {
        return ''
      }
      const name = (this.viewingContact.displayName || '').trim()
      const role = (this.viewingContact.roleLabel || '').trim()
      if (name && role) {
        return `${name} (${role})`
      }
      return name || role
    },
    defaultLeaderId() {
      if (this.hasViewingContact) {
        return CONTACT_PERSON_LEADER
      }
      return this.currentUserId || ''
    },
    activeUserIdSet() {
      return new Set((this.activeUsers || []).map(user => user.id).filter(Boolean))
    },
    contactHasSubmittedAppointments() {
      const contactId = this.viewingContact?.id
      if (!contactId) {
        return false
      }
      return (this.existingAppointments || []).some(appointment => {
        if (appointment.status === SchedulerAppointmentStatus.APPOINTMENT_STATUS_CANCELLED) {
          return false
        }
        if (appointment.creatorId !== contactId) {
          return false
        }
        // Contact-submitted slots have a contactId creator, not an account user id
        return !this.activeUserIdSet.has(appointment.creatorId)
      })
    }
  },
  watch: {
    defaultLeaderId: {
      immediate: true,
      handler(newVal) {
        if (!newVal) {
          return
        }
        if (this.editableRows.length === 1) {
          const editable = this.editableRows[0]
          if (!editable.date && !editable.from && !editable.leaderId) {
            editable.leaderId = newVal
          }
        }
      }
    },
    addMore: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.loadExistingAppointments()
        }
      }
    },
    hasViewingContact(newVal) {
      if (!newVal) {
        return
      }
      this.rows.forEach(row => {
        if (row.locked && !row.leaderId) {
          row.leaderId = CONTACT_PERSON_LEADER
        }
        if (!row.locked && !row.date && !row.from && !row.leaderId) {
          row.leaderId = this.defaultLeaderId
        }
      })
    }
  },
  mounted() {
    this.timeList = buildQuarterHourTimeList()
    if (!this.rows[0]?.leaderId && this.defaultLeaderId) {
      this.rows[0].leaderId = this.defaultLeaderId
    }
    this.emitDraftSlots()
  },
  methods: {
    createRow() {
      return createEmptyRow(this.defaultLeaderId)
    },
    isContactLeader(leaderId) {
      return leaderId === CONTACT_PERSON_LEADER
    },
    appointmentToLockedRow(appointment) {
      const calendarUserId = appointment.calendarUserId || ''
      return {
        date: this.objectToIsoDate(appointment.date) || '',
        from: padTimePart(appointment.startTime?.hours || 0) + ':' + padTimePart(appointment.startTime?.minutes || 0),
        to: padTimePart(appointment.endTime?.hours || 0) + ':' + padTimePart(appointment.endTime?.minutes || 0),
        maxParticipants: appointment.maxParticipants || '',
        leaderId: calendarUserId || (this.hasViewingContact ? CONTACT_PERSON_LEADER : ''),
        locked: true,
        appointmentId: appointment.id
      }
    },
    loadExistingAppointments() {
      if (!this.objectId || this.loadingExisting) {
        return
      }
      this.loadingExisting = true
      CalendarSchedulerApi.listAppointmentsByObjectId(this.objectId)
        .then(response => {
          this.existingAppointments = response.appointments || []
          const locked = this.existingAppointments
            .filter(appointment => appointment.status !== SchedulerAppointmentStatus.APPOINTMENT_STATUS_CANCELLED)
            .map(appointment => this.appointmentToLockedRow(appointment))
          const empty = this.createRow()
          this.rows = locked.concat([empty])
          this.rowErrors = this.rows.map(() => ({ date: null, from: null, to: null, leaderId: null, maxParticipants: null }))
          this.emitDraftSlots()
        })
        .catch(e => {
          console.log(e)
        })
        .finally(() => {
          this.loadingExisting = false
        })
    },
    addRow() {
      this.rows.push(this.createRow())
      this.rowErrors.push({ date: null, from: null, to: null, leaderId: null, maxParticipants: null })
      this.emitDraftSlots()
    },
    removeRow(index) {
      if (this.rows[index]?.locked || this.editableRows.length <= 1) {
        return
      }
      this.rows.splice(index, 1)
      this.rowErrors.splice(index, 1)
      this.emitDraftSlots()
    },
    onRowChanged(index) {
      if (this.rows[index]?.locked) {
        return
      }
      if (this.rowErrors[index]) {
        this.$set(this.rowErrors[index], 'date', null)
        this.$set(this.rowErrors[index], 'from', null)
        this.$set(this.rowErrors[index], 'to', null)
        this.$set(this.rowErrors[index], 'leaderId', null)
        this.$set(this.rowErrors[index], 'maxParticipants', null)
      }
      this.emitDraftSlots()
      const row = this.rows[index]
      if (row?.date) {
        this.$emit('focus-date-changed', row.date)
      }
    },
    emitDraftSlots() {
      const drafts = this.rows
        .map((row, index) => ({ row, index }))
        .filter(({ row }) => {
          if (row.locked || !row.date || !row.from || !row.to || !row.leaderId) {
            return false
          }
          return !this.isContactLeader(row.leaderId)
        })
        .map(({ row, index }) => ({
          id: `draft-schedule-${index}`,
          date: row.date,
          from: row.from,
          to: row.to,
          calendarUserId: row.leaderId,
          title: this.$t('message.marketingObjects.viewingAppointments.draftEventTitle')
        }))
      this.$emit('draft-slots-changed', drafts)
    },
    validate() {
      let valid = true
      let hasPast = false
      let hasOverlap = false
      let hasStartAfterEnd = false
      let hasZeroMaxParticipants = false
      const editableIndices = []
      this.rowErrors = this.rows.map((row, index) => {
        if (!row.locked) {
          editableIndices.push(index)
        }
        return { date: null, from: null, to: null, leaderId: null, maxParticipants: null }
      })

      if (!editableIndices.length) {
        return false
      }

      editableIndices.forEach(index => {
        const row = this.rows[index]
        if (!row.date) {
          this.rowErrors[index].date = false
          valid = false
        }
        if (!row.from) {
          this.rowErrors[index].from = false
          valid = false
        }
        if (!row.to) {
          this.rowErrors[index].to = false
          valid = false
        }
        const fromMin = timeToMinutes(row.from)
        const toMin = timeToMinutes(row.to)
        if (fromMin !== null && toMin !== null && fromMin >= toMin) {
          this.rowErrors[index].from = false
          this.rowErrors[index].to = false
          hasStartAfterEnd = true
          valid = false
        }
        if (!row.leaderId) {
          this.rowErrors[index].leaderId = false
          valid = false
        } else if (this.isContactLeader(row.leaderId) && !this.hasViewingContact) {
          this.rowErrors[index].leaderId = false
          valid = false
        } else if (!this.isContactLeader(row.leaderId) && !this.activeUserIdSet.has(row.leaderId)) {
          this.rowErrors[index].leaderId = false
          valid = false
        }
        if (isRowStartInPast(row)) {
          this.rowErrors[index].date = false
          this.rowErrors[index].from = false
          hasPast = true
          valid = false
        }
        if (this.isZeroMaxParticipants(row.maxParticipants)) {
          this.rowErrors[index].maxParticipants = false
          hasZeroMaxParticipants = true
          valid = false
        }
      })

      for (let i = 0; i < this.rows.length; i++) {
        for (let j = i + 1; j < this.rows.length; j++) {
          if (rowsOverlap(this.rows[i], this.rows[j])) {
            // Overlap with locked rows is also invalid for new entries
            if (!this.rows[i].locked || !this.rows[j].locked) {
              hasOverlap = true
              valid = false
            }
          }
        }
      }

      if (hasPast) {
        Vue.toasted.show(this.$t('message.marketingObjects.viewingAppointments.errors.pastStart'), { type: 'error' })
      }
      if (hasStartAfterEnd) {
        Vue.toasted.show(this.$t('message.marketingObjects.viewingAppointments.errors.startAfterEnd'), { type: 'error' })
      }
      if (hasOverlap) {
        Vue.toasted.show(this.$t('message.marketingObjects.viewingAppointments.errors.overlap'), { type: 'error' })
      }
      if (hasZeroMaxParticipants) {
        Vue.toasted.show(this.$t('message.marketingObjects.viewingAppointments.errors.maxParticipantsZero'), { type: 'error' })
      }
      return valid
    },
    isZeroMaxParticipants(value) {
      if (value === '' || value === null || value === undefined) {
        return false
      }
      const max = parseInt(value, 10)
      return !Number.isNaN(max) && max === 0
    },
    buildSlots() {
      return this.editableRows.map(row => {
        const date = this.toObject(row.date)
        const startTime = parseTime(row.from)
        const endTime = parseTime(row.to)
        const slot = {
          date,
          startTime,
          endTime
        }
        if (row.maxParticipants !== '' && row.maxParticipants !== null && row.maxParticipants !== undefined) {
          const max = parseInt(row.maxParticipants, 10)
          if (!Number.isNaN(max) && max > 0) {
            slot.maxParticipants = max
          }
        }
        if (row.leaderId && !this.isContactLeader(row.leaderId)) {
          slot.calendarUserId = row.leaderId
        }
        return slot
      })
    },
    shouldShowInformContactModal() {
      const hasContactLedRow = this.editableRows.some(row => this.isContactLeader(row.leaderId))
      return hasContactLedRow && this.contactHasSubmittedAppointments
    },
    save() {
      if (!this.validate()) {
        return
      }
      const slots = this.buildSlots()
      if (!slots.length) {
        return
      }
      if (this.shouldShowInformContactModal()) {
        this.$nextTick(() => {
          this.$refs.informViewingContactModal?.show()
        })
        return
      }
      this.persistSlots(slots, false)
    },
    onInformContactCancel() {
      this.$refs.informViewingContactModal?.hide()
    },
    onInformContactConfirm(informContact) {
      this.$refs.informViewingContactModal?.hide()
      const slots = this.buildSlots()
      if (!slots.length) {
        return
      }
      this.persistSlots(slots, informContact)
    },
    persistSlots(slots, informContact) {
      this.saving = true
      CalendarSchedulerApi.createAppointments(this.objectId, slots)
        .then(() => {
          this.saving = false
          this.$emit('saved', { informContact: !!informContact })
        })
        .catch(e => {
          console.log(e)
          this.saving = false
          Vue.toasted.show(this.$t('message.savingErrors.general'), { type: 'error' })
        })
    }
  }
}
</script>

<style scoped>
.schedule-row-locked {
  opacity: 0.65;
  pointer-events: none;
}
.schedule-row-locked :deep(.form-control),
.schedule-row-locked :deep(select) {
  background-color: #f2f4f5;
}
</style>
